import type { UflrType } from 'lib/uflr';
import type { DomainAccess } from 'reactApp/components/SharingWindow/Sharing.types';
import type { WeblinkDownloadable, WeblinkDownloadsCounts } from 'reactApp/modules/public/public.types';
import type { EStorageType } from 'reactApp/modules/storage/storage.types';
import type { AccessRights, Count, Kind, Sort, SubKind, Url, VirusScan, Weblink } from 'reactApp/types/Tree';

export interface Item {
    name: string;
    home: string;
    weblink?: string;
    weblinkAccessRights?: AccessRights;
    weblinkExpires?: number;
    weblinkDomestic?: boolean;
    weblinkDownloadable?: boolean;
    weblinkSeoEnabled?: boolean;
    weblinkEnabledDisabledDownload?: boolean;
    ext?: string;
    id: string; // вычисляемое; если мы в хомяке - home, если в паблике - weblink
    size: number;
    type: string;
    parent: string; // вычисляемое
    descriptorId?: string;
    count_downloads_total?: number;
    count_downloads_left?: number;
    __reduxTree: true;
    intraDomainShare?: boolean;
    mode_bitmask?: number;
}

export interface IFolderFlags {
    isMedia?: boolean;
}

export interface HomeFile extends WeblinkDownloadable {
    id: string;
    isFolder: false;
    parent: string;
    virusScan: VirusScan;
    ext: string;
    nameWithoutExt: string;
    isInFavorites: boolean;
    isMounted: boolean;
    name: string;
    home: string;
    author?: string;
    size: number;
    kind: Kind;
    descriptorId?: string;
    subKind: SubKind;
    attr?: {
        mandate?: string;
        actor: string;
    };
    weblink?: string;
    weblinkAccessRights?: AccessRights;
    weblinkExpires?: number;
    ctime?: number;
    expiresTimeSize?: number;
    weblinkDomestic?: boolean;
    weblinkDomainAccess?: DomainAccess;
    weblinkSeoEnabled?: boolean;
    weblinkEnabledDisabledDownload?: boolean;
    uniqueId?: string;
    mtime: number;
    hash: string;
    __reduxTree: boolean;
    storage: EStorageType.home;
    thumbnails?: {
        [key: string]: string;
    };
    url?: Url;
    uflr?: UflrType;
    isReadOnly?: boolean;
    intraDomainShare?: boolean;
    // tempexp_18662-start
    weblink_views?: number;
    weblink_downloads?: number;
    // tempexp_18662-end
    mode_bitmask?: number;
}

export interface HomeFolder extends WeblinkDownloadable {
    id: string;
    home: string;
    name: string;
    weblink?: string;
    weblinkAccessRights?: AccessRights;
    weblinkExpires?: number;
    ctime?: number;
    expiresTimeSize?: number;
    weblinkDomestic?: boolean;
    weblinkDomainAccess?: DomainAccess;
    weblinkAutoDelete?: boolean;
    weblinkSeoEnabled?: boolean;
    weblinkEnabledDisabledDownload?: boolean;
    uniqueId?: string;
    isInFavorites: boolean;
    isMounted: boolean;
    parent: string;
    isFolder: true;
    storage: EStorageType.home;
    rev: number;
    size: number;
    quota?: number;
    tree: string;
    count: Count;
    sort?: Sort;
    kind: Kind;
    descriptorId?: string;
    isReadOnly: boolean;
    isLoading: boolean;
    isLoaded: boolean;
    error?: string;
    hasMoreToLoad: boolean;
    childs: string[];
    authors?: Record<string, number>;
    __reduxTree: true;
    info?: {
        owner: { email: string; name: string };
        tree: string;
        invited: { email: string; status: string; access: string; name: string }[];
        access: string;
        name: string;
        size: number;
        invite_token: string;
        id: string;
        invite: { email: string; status: string; access: string; name: string };
    };
    flags?: IFolderFlags;
    intraDomainShare?: boolean;
    // tempexp_18662-start
    weblink_views?: number;
    weblink_downloads?: number;
    // tempexp_18662-end
    mode_bitmask?: number;
}

export type HomeItem = HomeFolder | HomeFile;

interface LegacyItem extends WeblinkDownloadable {
    weblink?: string;
    absoluteFolderPath?: string;
    name: string;
    size: number;
    kind: Kind;
    type: string;
    home?: string;
    id: string;
    isFolder: boolean;
    virusStatus: null | boolean;
    subKind: string;
    __isItem: boolean;
    parent: string;
    url: Url;
    __normalizeUrls: number; // ??
    download: boolean; // ??
    sizeTitle: string;
    __size: number;
    href: string;
    publishingStatus: string;
    icon: string;
    favoritesEnabled: boolean;
    __normalizeProps: number; // ??
    mode_bitmask?: number;
    thumbnails?: {
        [key: string]: string;
    };
}

export interface LegacyFile extends LegacyItem {
    virus_scan: VirusScan;
    filename: string;
    mtime: number;
    ext2: string;
    ext: string;
    attr: {
        actor: string;
    };
    hash: string;
    isInFavorites: boolean;
}

export interface ApiFolderResponseItem {
    home: string;
    weblink?: string;
    weblink_access_rights?: AccessRights;
    weblink_expires?: number;
    weblink_domestic?: boolean;
    name: string;
    type: string;
    kind: Kind;
    size: number;
    quota?: number;
    public?: Weblink;
    descriptorId?: string;
    mode_bitmask?: number;
}

export interface APILoadFolderFail {
    id: string;
    error: string;
}

export interface ApiFolderResponseFile extends ApiFolderResponseItem, WeblinkDownloadsCounts {
    attr?: {
        mandate?: string;
        actor: string;
    };
    flags?: {
        mounted: boolean;
    };
    hash: string;
    virus_scan: VirusScan;
    mtime: number;
    favorite?: boolean;
    uflr?: UflrType;
    intra_domain_share?: boolean;
}

export interface ApiFolderResponse extends ApiFolderResponseItem, WeblinkDownloadsCounts {
    count: {
        folders: number;
        files: number;
    };
    rev: number;
    grev: number;
    sort?: Sort;
    tree: string;
    readonly?: boolean;
    favorite?: boolean;
    list?: (ApiFolderResponse | ApiFolderResponseFile)[];
    flags?: {
        mounted?: boolean;
        is_gallery?: boolean;
    };
    intra_domain_share?: boolean;
    mode_bitmask?: number;
}

export interface MediaStatistics {
    photos?: number | null;
    videos?: number | null;
    size?: number | null;
}

export interface FilesState {
    list: {
        [key: string]: HomeItem;
    };
    homeStatistics?: MediaStatistics | null;
    cameraUploadsStatistics?: MediaStatistics | null;
    domainFoldersFilterActive?: boolean;
    intraDomainShare?: boolean;
    lastOpenFolder?: string | null;
    redirectFromViewerItem?: string | null;
}

export type NormalizeFunc = (props: {
    state: FilesState;
    item: ApiFolderResponse | ApiFolderResponseFile;
    loaded?: boolean;
    parent?: string;
    isLoading?: boolean;
    // Если мы уже загрузили ранее всех детей в папке, а в новом ответе по апи их нет, потому сохраняем уже загруженные.
    // Например, загрузили все до /Тест1/Тест2
    // Потом прилетел апи ответ по корню, в нем будет папка Тест1, но без детей
    keepPreviousChilds?: boolean;
}) => void;

export type NormalizeFileFunc = (props: { item: ApiFolderResponseFile; normalizedItem: Item }) => HomeFile;
export type NormalizeFolderFunc = (props: { item: ApiFolderResponse; normalizedItem: Item; isLoaded: boolean }) => HomeFolder;

export const enum XrayItemType {
    file = 'file',
    folder = 'folder',
    album = 'album',
}
